/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Database, RefreshCw, CheckCircle2, Loader2, AlertCircle, Clock,
} from "lucide-react";


const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-DO", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtBytes = (b) => {
  if (!b) return "—";
  const mb = b / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
};


export default function DgiiRncRegistryPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dgii/rnc/status");
      setStatus(data);
      // Si está corriendo, polling cada 5s
      if (data?.status === "running") {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (e) {
      toast.error("No se pudo cargar el estado: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling]);

  const triggerSync = async () => {
    try {
      await api.post("/dgii/rnc/sync-now");
      toast.success("Actualización iniciada. Toma 2-6 min (~800 mil empresas).");
      setPolling(true);
      await load();
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    }
  };

  const isRunning = status?.status === "running";
  const isOk = status?.status === "ok";
  const hasError = status?.status === "error";
  const totalRncs = status?.total_in_db || 0;

  return (
    <Card data-testid="dgii-rnc-panel">
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-primary" />
            <div>
              <CardTitle className="text-lg">Padrón RNC DGII</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Base de datos de contribuyentes dominicanos para validar nuevos clientes
                y proveedores. Se sincroniza automáticamente a las 4am todos los días.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
                    data-testid="dgii-rnc-reload">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Recargar
            </Button>
            <Button size="sm" onClick={triggerSync} disabled={isRunning}
                    data-testid="dgii-rnc-sync-now">
              {isRunning ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sincronizando…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-1" /> Actualizar ahora</>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatBox
            icon={<Database className="w-4 h-4" />}
            label="Contribuyentes"
            value={totalRncs.toLocaleString("es-DO")}
            testid="dgii-rnc-total"
          />
          <StatBox
            icon={<Clock className="w-4 h-4" />}
            label="Última sync"
            value={fmtDate(status?.last_success_at)}
            testid="dgii-rnc-last"
          />
          <StatBox
            icon={<RefreshCw className="w-4 h-4" />}
            label="Duración"
            value={status?.duration_s ? `${status.duration_s.toFixed(1)}s` : "—"}
            testid="dgii-rnc-duration"
          />
          <StatBox
            icon={<Database className="w-4 h-4" />}
            label="Tamaño ZIP"
            value={fmtBytes(status?.zip_bytes)}
            testid="dgii-rnc-size"
          />
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Estado:</span>
          {isRunning && (
            <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sincronizando
            </Badge>
          )}
          {isOk && (
            <Badge className="bg-success/15 text-success border-success/30">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Al día
            </Badge>
          )}
          {hasError && (
            <Badge className="bg-destructive/15 text-destructive border-destructive/30">
              <AlertCircle className="w-3 h-3 mr-1" /> Error
            </Badge>
          )}
          {!status?.status && totalRncs === 0 && (
            <Badge variant="outline">Pendiente</Badge>
          )}
          {isRunning && (
            <span className="text-xs text-muted-foreground" data-testid="dgii-rnc-progress">
              {(status?.progress_rows || 0).toLocaleString("es-DO")} empresas procesadas…
            </span>
          )}
        </div>
        {hasError && status?.error && (
          <p className="mt-2 text-xs text-destructive break-words" data-testid="dgii-rnc-error">{status.error}</p>
        )}
        {totalRncs > 0 && totalRncs < 100000 && !isRunning && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-sm" data-testid="dgii-rnc-incomplete">
            <strong>Padrón incompleto.</strong> Solo hay {totalRncs.toLocaleString("es-DO")} empresas (el padrón completo tiene ~800 mil).
            Pulsa "Actualizar ahora". Si la DGII bloquea la descarga desde el servidor, usa en el servidor:{" "}
            <code className="text-xs">sudo bash /opt/ferreteria/deploy/sync_dgii_rnc.sh --file RNC_Contribuyentes.zip</code>
          </div>
        )}

        {totalRncs === 0 && !isRunning && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-sm">
            <strong>Padrón vacío.</strong> La primera descarga toma 1-2 min y
            carga ~800k contribuyentes. Pulsa "Actualizar ahora".
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function StatBox({ icon, label, value, testid }) {
  return (
    <div className="p-3 bg-muted/40 rounded-md border border-border/60"
         data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="font-mono text-sm font-semibold data-cell truncate">
        {value}
      </div>
    </div>
  );
}
